import { supabase } from '../supabaseClient';
import { requireSession } from '../lib/auth/requireSession';

/**
 * Published festival map as one GeoJSON FeatureCollection, or null when the
 * event has no published+ready map — which is the signal to render Find
 * without the map toggle at all. Single RPC round trip; the result lands in
 * the persisted query cache, which is what makes the map work offline.
 */
export async function fetchFestivalMap(eventId) {
  if (draftMapRequested()) {
    const draft = await fetchDraftMap(eventId);
    if (draft) return draft;
  }
  const { data, error } = await supabase.rpc('get_festival_map', {
    p_event_id: eventId,
  });
  if (error) throw error;
  return data ?? null;
}

/**
 * Local preview of a pipeline build, dev only: open the map with `?draft=1` and
 * the payload comes from public/draft-maps/<eventId>.json (written by
 * scripts/festival_map_3d/app/preview_payload.py) instead of the database.
 * Nothing is written anywhere; a missing file falls back to the published map.
 */
export function draftMapRequested() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('draft') === '1';
}

async function fetchDraftMap(eventId) {
  try {
    const res = await fetch(`/draft-maps/${encodeURIComponent(eventId)}.json`, {
      cache: 'no-store',
    });
    if (!res.ok) {
      console.warn(
        `[map] no draft payload for ${eventId} (${res.status}); showing the published map`,
      );
      return null;
    }
    const payload = await res.json();
    // properties.edition_id is the event's WORKING (unpublished) edition when the builder found one - a draft preview
    // saves there, never to the live edition (operator, 2026-09-21). The client cannot look editions up itself (no
    // grants; maps come through RPCs), so build_festival.py stamps it, and pull_working_scene.py brings saved work back.
    return payload;
  } catch (err) {
    console.warn('[map] draft payload failed to load; showing the published map', err);
    return null;
  }
}

/** The caller's events that have a published, ready map — the Maps index. Order is the RPC's; do not re-sort. */
export async function fetchMyFestivalMaps() {
  // Asked as nobody (a token mid-refresh), the RPC answers [] and that was cached as "going to nothing" (requireSession).
  await requireSession();
  const { data, error } = await supabase.rpc('list_my_festival_maps');
  if (error) throw error;
  return data ?? [];
}

/** Every upcoming event with a published, ready map (the index's "Upcoming"), soonest first. Past events are never listed. */
export async function fetchUpcomingFestivalMaps() {
  const { data, error } = await supabase.rpc('list_upcoming_festival_maps');
  if (error) throw error;
  return data ?? [];
}
